/**
 * Label — Text label for form elements or section headings.
 *
 * Label Component Guidelines
 * - In forms, prefer `<FieldLabel>` (which wraps Label) for automatic Field
 *   integration. Use standalone Label only in non-form contexts like section
 *   headings or sidebar group titles.
 * - Size should match the associated input: `xs` for dense utility UIs, `sm` for
 *   compact forms inside popovers, `base` for standard page-level forms.
 * - Label automatically dims and disables pointer events when its parent Field is
 *   disabled — no extra handling needed.
 * @see https://ui.shadcn.com/docs/components/label shadcn/ui Label documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const labelVariants = cva(
  "text-default gap-2 leading-none font-medium group-data-[disabled=true]:opacity-50 peer-disabled:opacity-50 flex items-center select-none group-data-[disabled=true]:pointer-events-none peer-disabled:cursor-not-allowed",
  {
    variants: {
      size: {
        xs: "text-2xs",
        sm: "text-xs",
        base: "text-sm",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

type LabelSizes = VariantProps<typeof labelVariants>["size"];

interface LabelProps<ClassName extends string = string>
  extends
    Omit<React.ComponentProps<"label">, "className">,
    VariantProps<typeof labelVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function Label<ClassNameType extends string = string>({
  UNSAFE_className,
  size,
  className,
  ...props
}: LabelProps<ClassNameType>) {
  return (
    <label
      data-slot="label"
      className={cn(labelVariants({ size }), className, UNSAFE_className)}
      {...props}
    />
  );
}

export { Label, type LabelSizes };
